import React, { useMemo } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import { Container, Row, Col, Card, Badge, Button } from "react-bootstrap";
import SearchArticle from "../components/SearchArticle";
import BreadcrumbBar from "../components/Breadcrumb";
import { FiBookmark } from "react-icons/fi";
import { PiPrinterLight } from "react-icons/pi";
import { LuShare2 } from "react-icons/lu";
import { GrExpand } from "react-icons/gr";

const RELATED = [
  {
    id: 2,
    title:
      "Precision Psychiatry Applications with Pharmacogenomics: Artificial Intelligence and Machine Learning Approaches",
    authors: ["Wang Q", "Xiao ZX", "Zheng X", "Xiang N", "Schott G"],
    journal: "N Engl J Med.",
    date: "2025 Sep 18;393(11):1051-1064",
    doi: "10.1056/NEJMoa2504650",
    pmid: "26978177",
    labels: ["Clinical Trial"],
  },
  {
    id: 3,
    title:
      "Association among basal serum BDNF, cardiorespiratory fitness and cardiovascular disease risk factors in untrained healthy Korean men",
    authors: ["Wang Q", "Xiao ZX", "Zheng X", "Xiang N", "Schott G"],
    journal: "N Engl J Med.",
    date: "2025 Sep 18;393(11):1051-1064",
    doi: "10.1056/NEJMoa2504650",
    pmid: "26978177",
    labels: ["Clinical Trial"],
  },
  {
    id: 4,
    title:
      "In Vivo CD19 CAR T-Cell Therapy for Refractory Systemic Lupus Erythematosus",
    authors: ["Wang Q", "Xiao ZX", "Zheng X", "Xiang N", "Schott G"],
    journal: "N Engl J Med.",
    date: "2025 Sep 18;393(11):1051-1064",
    doi: "10.1056/NEJMoa2504650",
    pmid: "26978177",
    labels: ["Clinical Trial"],
  },
  {
    id: 5,
    title:
      "Fully Human Anti-CD19 CAR T Cells Derived from Systemic Lupus Erythematosus Patients Exhibit Cytotoxicity with Reduced Inflammatory Cytokine Production",
    authors: ["Wang Q", "Xiao ZX", "Zheng X", "Xiang N", "Schott G"],
    journal: "N Engl J Med.",
    date: "2025 Sep 18;393(11):1051-1064",
    doi: "10.1056/NEJMoa2504650",
    pmid: "26978177",
    labels: ["Clinical Trial"],
  },
];

export default function ExploreDetails() {
  const { id } = useParams();
  const { state } = useLocation();

  // Prefer forwarded state; otherwise read from window-scoped demo list (or replace with API fetch).
  const item = useMemo(() => {
    if (state?.item) return state.item;
    const list = window.__DEMO_RESULTS__ || [];
    return list.find((r) => String(r.id) === String(id));
  }, [id, state]);

  // Related list (simple demo: everything except current id)
  const related = useMemo(() => {
    const list = window.__DEMO_RESULTS__ || [];
    return list.filter((r) => String(r.id) !== String(id));
  }, [id]);

  if (!item) {
    return (
      <Container className="py-4">
        <p className="mb-3">
          <Button as={Link} to="/" variant="outline-secondary" size="sm">
            ← Back
          </Button>
        </p>
        <Card body>
          We couldn’t find this article. Try going back to the list.
        </Card>
      </Container>
    );
  }

  return (
    <>
      <SearchArticle />
      <BreadcrumbBar
        items={[
          { label: "Home", href: "/" },
          { label: "Articles", href: "/explore" },
          { label: item.title },
        ]}
      />
      <Container className="explore-details">
        {/* Top action bar */}

        <Row>
          {/* LEFT: Main article */}
          <Col lg={8}>
            <div className="action-bar">
              <Button size="sm" variant="light" className="action-pill">
                <FiBookmark /> SAVE
              </Button>
              <Button size="sm" variant="light" className="action-pill">
                <PiPrinterLight /> PRINT
              </Button>
              <Button size="sm" variant="light" className="action-pill">
                <LuShare2 /> SHARE
              </Button>
            </div>
            <div className="article-main">
              {/* Hero image */}
              {item.image && (
                <img
                  alt="article"
                  className="hero-image"
                  src={
                    item.image ||
                    "https://images.unsplash.com/photo-1581093588401-16ec48319a7c?q=80&w=1600&auto=format&fit=crop"
                  }
                />
              )}

              {/* Title */}
              <h1 className="article-title">{item.title}</h1>

              {/* Author chips */}
              <div className="author-chips">
                {(item.authors || []).map((a, i) => (
                  <Badge key={i} className="author-badge">
                    {a}
                  </Badge>
                ))}
              </div>

              {/* Affiliations row (DOI/PMID/label left; Expand button right) */}
              <div className="affiliations-row">
                Affiliations
                <Button size="sm" variant="light" className="expand-btn">
                  Expand{" "}
                  <span>
                    <GrExpand />
                  </span>
                </Button>
              </div>

              <div className="aff-meta">
                <div className="meta-line">
                  <span className="meta-key">PMID:</span> {item.pmid || "—"}
                </div>
                <div className="meta-line">
                  <span className="meta-key">DOI:</span>{" "}
                  <a href="#" className="doi-link">
                    {item.doi || "—"}
                  </a>
                </div>
                <div className="meta-tag">Clinical Trial</div>
              </div>

              {/* Abstract */}
              <div className="abstract-wrap">
                <div className="section-head">Abstract</div>
                <p className="abstract-text">
                  {item.abstract ||
                    `Decision-making in healthcare relies on understanding patients' past and
                  current health states to predict and, ultimately, change their future
                  course...`}
                </p>
              </div>
              <span className="des-date">2025 Sep 17 | Author name</span>
            </div>
          </Col>

          {/* RIGHT: Related articles */}
          <Col lg={4}>
            <Card className="related-articles">
              <Card.Body>
                <div className="related-title">Related articles</div>

                <div className="related-list">
                  {RELATED.map((r) => (
                    <div key={r.id} className="related-item">
                      <Link
                        to={`/explore/${r.id}`}
                        state={{ item: r }}
                        className="rel-headline"
                      >
                        {r.title}
                      </Link>

                      <div className="rel-authors">
                        {(r.authors || []).slice(0, 5).map((a, i) => (
                          <Badge key={i} className="rel-chip">
                            {a}
                          </Badge>
                        ))}
                      </div>

                      <div className="rel-meta">
                        <div className="rel-meta-line">
                          PMID: {r.pmid || "—"}
                        </div>
                        <div className="rel-tag">Clinical Trial</div>
                      </div>

                      <Button
                        as={Link}
                        to={`/explore/${r.id}`}
                        state={{ item: r }}
                        size="sm"
                        variant="light"
                        className="read-more"
                      >
                        READ MORE
                      </Button>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}
